import React from "react";
import { useRootStore } from "../../../../store/contexts";
import { ShowMultiLine } from "../../../common/controls";
import { ShowReference } from "../../../common/controls";
import { editionTypeDisplayId } from "../../../editionType/store/EditionTypeCommonStore";
import { ShowNumber } from "../../../common/controls";
import { ShowReferenceSet } from "../../../common/controls";
import { musicalWorkDisplayId } from "../../../musicalWork/store/MusicalWorkCommonStore";
import { GroupContainer } from "../../../../common/components";
import { ShowItemsCompact } from "../../../common/controls";
import { useSnapshotModalStack } from "../../../common/controls";
import { EditionTypeView as SnapshotEditionTypeView } from "./EditionTypeView";
import { MusicalWorkView as SnapshotMusicalWorkView } from "./MusicalWorkView";
import { PhonogramView as SnapshotPhonogramView } from "./PhonogramView";
import { MusicalWorkLinkView as SnapshotMusicalWorkLinkView } from "./MusicalWorkLinkView";
import { PlatformView as SnapshotPlatformView } from "./PlatformView";
import { PlatformLinkTypeView as SnapshotPlatformLinkTypeView } from "./PlatformLinkTypeView";
import { MusicalGenreView as SnapshotMusicalGenreView } from "./MusicalGenreView";
import { InterpreterView as SnapshotInterpreterView } from "./InterpreterView";
import { TerritoryView as SnapshotTerritoryView } from "./TerritoryView";
import { WorkAuthorView as SnapshotWorkAuthorView } from "./WorkAuthorView";
import { AuthorComposerView as SnapshotAuthorComposerView } from "./AuthorComposerView";
import { CountryView as SnapshotCountryView } from "./CountryView";
import { ProvinceStateView as SnapshotProvinceStateView } from "./ProvinceStateView";
import { AuthorCollectingSocietyView as SnapshotAuthorCollectingSocietyView } from "./AuthorCollectingSocietyView";
import { CollectiveManagementSocietyView as SnapshotCollectiveManagementSocietyView } from "./CollectiveManagementSocietyView";
import { WorkAuthorPublisherView as SnapshotWorkAuthorPublisherView } from "./WorkAuthorPublisherView";
import { PublisherView as SnapshotPublisherView } from "./PublisherView";
import { PublisherRepresentativeView as SnapshotPublisherRepresentativeView } from "./PublisherRepresentativeView";
import { PublisherCollectingSocietyView as SnapshotPublisherCollectingSocietyView } from "./PublisherCollectingSocietyView";
import { EditionDocumentationView as SnapshotEditionDocumentationView } from "./EditionDocumentationView";
import { EditionContractPublisherView as SnapshotEditionContractPublisherView } from "./EditionContractPublisherView";
import { AssociatedDocumentEditionView as SnapshotAssociatedDocumentEditionView } from "./AssociatedDocumentEditionView";
import { EditionDocumentTypeView as SnapshotEditionDocumentTypeView } from "./EditionDocumentTypeView";
import { UserAttachmentEditionView as SnapshotUserAttachmentEditionView } from "./UserAttachmentEditionView";
import {
  useSnapshotExtension,
  useSnapshotExtensionTop,
} from "../../../common/controls";
import { FormControl } from "@mui/material";

type EditionViewProps = {
  snapshotData: any;
  // Propagated from the top-level snapshot view so nested tables can resolve
  // the snapshot-scoped file-download endpoint (/<snapshotEntityName>.data/
  // <snapshotRecordId>/<fileUuid>). Both are optional at the type level but
  // required at runtime for the download / view affordances to work.
  snapshotEntityName?: string;
  snapshotRecordId?: string;
};

export const EditionView: React.FC<EditionViewProps> = ({
  snapshotData,
  snapshotEntityName,
  snapshotRecordId,
}) => {
  const { langStore } = useRootStore();
  const renderExtensionTop = useSnapshotExtensionTop("Edition");
  const renderExtension = useSnapshotExtension("Edition");

  const { pushModal, modalElement } = useSnapshotModalStack(
    (entityName, data) => {
      switch (entityName) {
        case "EditionType":
          return (
            <SnapshotEditionTypeView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "MusicalWork":
          return (
            <SnapshotMusicalWorkView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Phonogram":
          return (
            <SnapshotPhonogramView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "MusicalWorkLink":
          return (
            <SnapshotMusicalWorkLinkView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Platform":
          return (
            <SnapshotPlatformView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "PlatformLinkType":
          return (
            <SnapshotPlatformLinkTypeView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "MusicalGenre":
          return (
            <SnapshotMusicalGenreView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Interpreter":
          return (
            <SnapshotInterpreterView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Territory":
          return (
            <SnapshotTerritoryView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "WorkAuthor":
          return (
            <SnapshotWorkAuthorView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "AuthorComposer":
          return (
            <SnapshotAuthorComposerView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Country":
          return (
            <SnapshotCountryView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "ProvinceState":
          return (
            <SnapshotProvinceStateView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "AuthorCollectingSociety":
          return (
            <SnapshotAuthorCollectingSocietyView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "CollectiveManagementSociety":
          return (
            <SnapshotCollectiveManagementSocietyView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "WorkAuthorPublisher":
          return (
            <SnapshotWorkAuthorPublisherView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Publisher":
          return (
            <SnapshotPublisherView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "PublisherRepresentative":
          return (
            <SnapshotPublisherRepresentativeView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "PublisherCollectingSociety":
          return (
            <SnapshotPublisherCollectingSocietyView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "EditionDocumentation":
          return (
            <SnapshotEditionDocumentationView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "EditionContractPublisher":
          return (
            <SnapshotEditionContractPublisherView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "AssociatedDocumentEdition":
          return (
            <SnapshotAssociatedDocumentEditionView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "EditionDocumentType":
          return (
            <SnapshotEditionDocumentTypeView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "UserAttachmentEdition":
          return (
            <SnapshotUserAttachmentEditionView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        default:
          return null;
      }
    }
  );
  return (
    <>
      {renderExtensionTop ? renderExtensionTop(snapshotData) : null}
      <GroupContainer
        id="editionData"
        title={langStore.groupEditionEditionDataLabel}
      >
        <FormControl fullWidth>
          <ShowMultiLine
            label={langStore.EditionNameLabel}
            value={String(snapshotData?.name ?? "")}
          />
          <ShowReference
            label={langStore.EditionEditionTypeLabel}
            selectedValue={
              snapshotData?.editionType
                ? langStore.displayIdByLang(
                    editionTypeDisplayId(snapshotData?.editionType, langStore)
                  )
                : ""
            }
            onView={() => pushModal("EditionType", snapshotData?.editionType)}
          />
          <ShowMultiLine
            label={langStore.EditionContentTypeLabel}
            value={String(snapshotData?.contentType ?? "")}
          />
          <ShowMultiLine
            label={langStore.EditionPublicationLinkLabel}
            value={String(snapshotData?.publicationLink ?? "")}
          />
          <ShowNumber
            label={langStore.EditionEditionNumberLabel}
            value={snapshotData?.editionNumber ?? null}
          />
          <ShowNumber
            label={langStore.EditionPriceLabel}
            value={snapshotData?.price ?? null}
            decimalScale={2}
            prefix={"$"}
            thousandSeparator={","}
            fixedDecimalScale={true}
          />
          <ShowReferenceSet
            label={langStore.EditionWorksLabel}
            selectedValues={(snapshotData?.works ?? []).map(
              (__x: any, __i: number) => ({
                id: String(__i),
                displayId: musicalWorkDisplayId(__x, langStore),
              })
            )}
            onView={(id) => {
              const idx = parseInt(id, 10);
              pushModal("MusicalWork", (snapshotData?.works ?? [])[idx]);
            }}
          />
        </FormControl>
      </GroupContainer>
      <GroupContainer
        id="editionContracts"
        title={
          (
            {
              es: "Documentación firmada",
              en: "Signed documentation",
              pt: "Documentação assinada",
            } as Record<string, string>
          )[langStore.language] ?? "Documentación firmada"
        }
      >
        <FormControl fullWidth>
          <ShowItemsCompact
            label={
              (
                {
                  es: "Documentación firmada",
                  en: "Signed documentation",
                  pt: "Documentação assinada",
                } as Record<string, string>
              )[langStore.language] ?? "Documentación firmada"
            }
            showLabel={false}
            enableDownload={true}
            entityName="EditionDocumentation"
            snapshotEntityName={snapshotEntityName}
            snapshotRecordId={snapshotRecordId}
            columns={[
              {
                name: "finalDocumentPdf",
                title: langStore.EditionDocumentationFinalDocumentPdfLabel,
              },
              {
                name: "authorsDisplay",
                title: langStore.EditionDocumentationAuthorsDisplayLabel,
              },
              {
                name: "publisherConfigurationDisplay",
                title:
                  langStore.EditionDocumentationPublisherConfigurationDisplayLabel,
              },
            ]}
            rows={(snapshotData?.documentation ?? []).map(
              (__r: any, __i: number) => ({
                __status__: "original",
                id: String(__i),
                finalDocumentPdf: __r?.finalDocumentPdf?.filename ?? "",
                finalDocumentPdf_data: __r?.finalDocumentPdf,
                authorsDisplay: __r?.authorsDisplay ?? "",
                publisherConfigurationDisplay:
                  __r?.publisherConfigurationDisplay ?? "",
              })
            )}
            onRowClick={(rowId: string) => {
              const idx = parseInt(rowId, 10);
              pushModal(
                "EditionDocumentation",
                (snapshotData?.documentation ?? [])[idx]
              );
            }}
          />
        </FormControl>
      </GroupContainer>
      <GroupContainer
        id="editionDocumentation"
        title={langStore.groupEditionEditionDocumentationLabel}
      >
        <FormControl fullWidth>
          <ShowItemsCompact
            label={langStore.EditionAssociatedDocumentsLabel}
            showLabel={false}
            enableDownload={true}
            entityName="AssociatedDocumentEdition"
            snapshotEntityName={snapshotEntityName}
            snapshotRecordId={snapshotRecordId}
            columns={[
              {
                name: "file",
                title: langStore.AssociatedDocumentEditionFileLabel,
              },
              {
                name: "authorsDisplay",
                title: langStore.AssociatedDocumentEditionAuthorsDisplayLabel,
              },
              {
                name: "publisherConfigurationDisplay",
                title:
                  langStore.AssociatedDocumentEditionPublisherConfigurationDisplayLabel,
              },
            ]}
            rows={(snapshotData?.associatedDocuments ?? []).map(
              (__r: any, __i: number) => ({
                __status__: "original",
                id: String(__i),
                file: __r?.file?.filename ?? "",
                file_data: __r?.file,
                authorsDisplay: __r?.authorsDisplay ?? "",
                publisherConfigurationDisplay:
                  __r?.publisherConfigurationDisplay ?? "",
              })
            )}
            onRowClick={(rowId: string) => {
              const idx = parseInt(rowId, 10);
              pushModal(
                "AssociatedDocumentEdition",
                (snapshotData?.associatedDocuments ?? [])[idx]
              );
            }}
          />
        </FormControl>
      </GroupContainer>
      <GroupContainer
        id="editionUserAttachments"
        title={langStore.groupEditionEditionUserAttachmentsLabel}
      >
        <FormControl fullWidth>
          <ShowItemsCompact
            label={langStore.EditionUserAttachmentsLabel}
            showLabel={false}
            enableDownload={true}
            entityName="UserAttachmentEdition"
            snapshotEntityName={snapshotEntityName}
            snapshotRecordId={snapshotRecordId}
            columns={[
              { name: "file", title: langStore.UserAttachmentEditionFileLabel },
              { name: "name", title: langStore.UserAttachmentEditionNameLabel },
            ]}
            rows={(snapshotData?.userAttachments ?? []).map(
              (__r: any, __i: number) => ({
                __status__: "original",
                id: String(__i),
                file: __r?.file?.filename ?? "",
                file_data: __r?.file,
                name: __r?.name ?? "",
              })
            )}
            onRowClick={(rowId: string) => {
              const idx = parseInt(rowId, 10);
              pushModal(
                "UserAttachmentEdition",
                (snapshotData?.userAttachments ?? [])[idx]
              );
            }}
          />
        </FormControl>
      </GroupContainer>
      {renderExtension ? renderExtension(snapshotData) : null}

      {modalElement}
    </>
  );
};
